import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { MapPin, RefreshCw, Wind, Droplets, Thermometer, Umbrella } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Widget météo réelle (Open-Meteo via backend). Ville modifiable et mémorisée.
export const WeatherWidget = () => {
  const [city, setCity] = useState(() => localStorage.getItem('capex_weather_city') || 'Paris');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(city);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (c) => {
    setLoading(true); setError(false);
    try {
      const token = localStorage.getItem('capex_token');
      const res = await axios.get(`${API}/weather`, {
        params: { city: c },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(true);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(city); }, [city, load]);

  const saveCity = () => {
    const c = draft.trim();
    if (c) { setCity(c); localStorage.setItem('capex_weather_city', c); }
    setEditing(false);
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 text-white px-6 py-4 shadow-lg w-full sm:w-auto sm:min-w-[300px]"
      data-testid="weather-widget"
    >
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
      <div className="relative flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-white/80 text-xs">
            <MapPin className="w-3 h-3" />
            {editing ? (
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveCity(); if (e.key === 'Escape') setEditing(false); }}
                onBlur={saveCity}
                className="bg-white/20 rounded px-1 py-0.5 text-white placeholder-white/60 text-xs outline-none w-24"
                placeholder="Ville…"
                data-testid="weather-city-input"
              />
            ) : (
              <button onClick={() => { setDraft(city); setEditing(true); }} className="hover:underline" data-testid="weather-city-btn">
                {data?.city || city}
              </button>
            )}
          </div>
          {loading ? (
            <p className="text-2xl font-bold mt-1">…</p>
          ) : error ? (
            <div className="mt-1">
              <p className="text-lg font-semibold leading-tight">Météo momentanément indisponible</p>
              <button onClick={() => load(city)} className="mt-1 inline-flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 rounded-full px-2.5 py-1 transition-colors" data-testid="weather-retry">
                <RefreshCw className="w-3 h-3" /> Réessayer
              </button>
            </div>
          ) : (
            <>
              <p className="text-3xl font-bold leading-tight mt-0.5 tabular-nums" data-testid="weather-temp">{data?.temperature}°C</p>
              <p className="text-xs text-white/90">{data?.condition}</p>
              {data?.feels_like != null && (
                <p className="text-[11px] text-white/80 flex items-center gap-1 mt-0.5"><Thermometer className="w-3 h-3" /> Ressenti {data.feels_like}°C</p>
              )}
            </>
          )}
        </div>
        <div className="text-5xl leading-none" data-testid="weather-icon">{loading ? '⏳' : (data?.icon || '🌡️')}</div>
      </div>
      {!loading && !error && (
        <div className="relative mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-white/85">
          {(data?.temp_min != null || data?.temp_max != null) && (
            <span className="flex items-center gap-1"><Thermometer className="w-3 h-3" /> {data?.temp_min}° / {data?.temp_max}°</span>
          )}
          {data?.precip_prob != null && (
            <span className="flex items-center gap-1"><Umbrella className="w-3 h-3" /> {data.precip_prob}% pluie</span>
          )}
          {data?.humidity != null && (
            <span className="flex items-center gap-1"><Droplets className="w-3 h-3" /> {data.humidity}% hum.</span>
          )}
          {data?.wind_speed != null && (
            <span className="flex items-center gap-1"><Wind className="w-3 h-3" /> {Math.round(data.wind_speed)} km/h</span>
          )}
        </div>
      )}
      <button
        onClick={() => load(city)}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
        title="Actualiser" data-testid="weather-refresh"
      >
        <RefreshCw className={`w-3.5 h-3.5 text-white/80 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default WeatherWidget;

